<template>
  <!-- Placeholder: standalone box (e.g. cock-fight during player switch) -->
  <div v-if="variant === 'placeholder'" class="video-loading-overlay video-loading-overlay--placeholder">
    <div class="video-loading-overlay__spinner">
      <div class="video-loading-overlay__spinner-icon video-loading-overlay__spinner-icon--md"></div>
      <p class="video-loading-overlay__message">{{ displayMessage }}</p>
    </div>
  </div>

  <!-- Overlay: covers video (reinitializing stream) -->
  <div
    v-else-if="variant === 'overlay'"
    class="video-loading-overlay video-loading-overlay--overlay"
    :style="{ zIndex }"
  >
    <div class="video-loading-overlay__spinner">
      <div class="video-loading-overlay__spinner-icon video-loading-overlay__spinner-icon--lg"></div>
      <p class="video-loading-overlay__message">{{ displayMessage }}</p>
    </div>
  </div>

  <!-- Error retry: icon + title + message + spinner -->
  <div
    v-else-if="variant === 'error-retry'"
    class="video-loading-overlay video-loading-overlay--error-retry"
    :style="{ zIndex }"
  >
    <div class="video-loading-overlay__error-content">
      <div class="video-loading-overlay__error-icon">{{ errorIcon }}</div>
      <p class="video-loading-overlay__error-title">{{ displayTitle }}</p>
      <p class="video-loading-overlay__error-message">{{ displayMessage }}</p>
      <div class="video-loading-overlay__retry-progress">
        <div class="video-loading-overlay__spinner-icon video-loading-overlay__spinner-icon--sm"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoLoadingOverlay',
  props: {
    /** 'placeholder' | 'overlay' | 'error-retry' */
    variant: {
      type: String,
      required: true,
      validator: (v) => ['placeholder', 'overlay', 'error-retry'].includes(v),
    },
    message: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    errorIcon: {
      type: String,
      default: '⚠️',
    },
    zIndex: {
      type: Number,
      default: 1003,
    },
  },
  computed: {
    displayMessage() {
      if (this.message) return this.message;
      if (this.variant === 'error-retry') return this.$t('Attempting to reconnect...');
      if (this.variant === 'overlay') return this.$t('Loading latest stream...');
      return this.$t('Loading...');
    },
    displayTitle() {
      return this.title || this.$t('Stream Connection Issue');
    },
  },
};
</script>

<style lang="scss" scoped>
.video-loading-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-loading-overlay--placeholder {
  width: 100%;
  aspect-ratio: 16 / 9;
  background-color: #0d1a24;

  // Fallback for browsers that don't support aspect-ratio
  @supports not (aspect-ratio: 16/9) {
    &::before {
      float: left;
      padding-top: 56.25%;
      content: '';
    }

    &::after {
      display: block;
      content: '';
      clear: both;
    }
  }
}

.video-loading-overlay--overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
}

.video-loading-overlay--error-retry {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(13, 26, 36, 0.95);
}

.video-loading-overlay__spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.video-loading-overlay--overlay .video-loading-overlay__spinner {
  gap: 16px;
}

.video-loading-overlay__spinner-icon {
  border-radius: 50%;
  animation: video-loading-spin 1s linear infinite;

  &--sm {
    width: 20px;
    height: 20px;
    border: 3px solid rgba(255, 255, 255, 0.2);
    border-top-color: #2483d5;
  }

  &--md {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255, 255, 255, 0.1);
    border-top-color: #f5a623;
  }

  &--lg {
    width: 50px;
    height: 50px;
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
  }
}

.video-loading-overlay__message {
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
}

.video-loading-overlay--overlay .video-loading-overlay__message {
  color: #fff;
  font-size: 16px;
  font-weight: 500;
}

.video-loading-overlay__error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: 400px;
}

.video-loading-overlay__error-icon {
  font-size: 36px;
  animation: video-loading-pulse 2s ease-in-out infinite;
}

.video-loading-overlay__error-title {
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  text-align: center;
}

.video-loading-overlay__error-message {
  color: #b2d6ea;
  font-size: 12px;
  margin: 0;
  text-align: center;
}

.video-loading-overlay__retry-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

@keyframes video-loading-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes video-loading-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}
</style>
